import {
  AspectRatio,
  Box,
  Button,
  DarkMode,
  Flex,
  HStack,
  Link,
  Stack,
  Text,
} from "@chakra-ui/react";
import PampaImage from "@raiden/library/components/PampaImage";
import {
  POSTS_TYPE_VALUE_ARTICLE,
  POSTS_TYPE_VALUE_ASSOCIATION,
  POSTS_TYPE_VALUE_EVENT,
} from "@raiden/library/constants/posts";
import useTranslate from "@raiden/library/hooks/useTranslate";
import generateFrontPath from "@raiden/library/libraries/utils/generateFrontPath";
import { useMemo } from "react";
import { FormattedDate, FormattedMessage } from "react-intl";
import { getPostPath } from "../../../helpers/posts/getPostPath";
import { Href } from "../../Href";
import { Icon } from "../../Icon";

/**
 * @typedef {Object} Props
 * @property {import("@raiden/library/types/Post").Post} post
 * @property {import("@chakra-ui/system").ResponsiveValue<number>} imageRatio
 * @property {import("react-hook-form").UseFormSetValue<{ category_id: string[] }>} setValue */
/**
 * @param {Props} props
 */
export function PostCard({ post, imageRatio, setValue }) {
  const translate = useTranslate();

  const title = post.current_revision?.title;
  const summary = post.current_revision?.summary;

  const isArticle = post.type === POSTS_TYPE_VALUE_ARTICLE;
  const isAssociation = post.type === POSTS_TYPE_VALUE_ASSOCIATION;
  const isEvent = post.type === POSTS_TYPE_VALUE_EVENT;

  const href = useMemo(() => {
    return getPostPath({
      postId: post.id,
      postType: post.type,
      postSlug: post.current_revision?.slug ?? null,
      translate,
    });
  }, [post, translate]);

  return (
    <Box position="relative" h="full" role="group" w="inherit">
      <Href href={href}>
        <Flex
          flexDirection="column"
          backgroundColor="brandPrimary.50"
          borderRadius="4px"
          overflow="hidden"
          h="full">
          <Box
            position="relative"
            backgroundColor="#fff"
            borderWidth="1px"
            borderBottomWidth="0"
            borderTopRadius="4px"
            overflow="hidden">
            <AspectRatio ratio={imageRatio}>
              <PampaImage
                image={post.current_revision?.cover}
                format={isAssociation ? "association" : "default"}
                mode="crop"
                placeholderSrc={generateFrontPath({
                  id: "@front.internal-assets",
                  parameters: { filePath: "placeholder.svg" },
                  includeBasePath: true,
                })}
                _groupHover={{
                  transform: "scale(1.1)",
                }}
                transition="all 0.25s ease"
                alt=""
              />
            </AspectRatio>

            {post.publication_date && isArticle && (
              <DarkMode>
                <Box position="absolute" top="0" left="0" p="8px">
                  <Box
                    backgroundColor="brandPrimary.700"
                    borderRadius="4px"
                    opacity={0.9}
                    py="6px"
                    px="8px">
                    <Text variant="menu-dropdown">
                      <FormattedDate
                        value={post.publication_date}
                        day="numeric"
                        month="long"
                        year="numeric"
                      />
                    </Text>
                  </Box>
                </Box>
              </DarkMode>
            )}

            {post.current_revision?.event_start_at && isEvent && (
              <DarkMode>
                <Box position="absolute" top="0" left="0" p="8px">
                  <Box
                    backgroundColor="brandPrimary.700"
                    borderRadius="4px"
                    opacity={0.9}
                    py="6px"
                    px="8px">
                    {post.current_revision?.event_end_at ? (
                      <HStack>
                        <Text as={HStack} variant="menu-dropdown">
                          <FormattedDate
                            value={post.current_revision.event_start_at}
                            day="numeric"
                            month="short"
                            year="numeric"
                          />

                          <Icon
                            icon="ms_chevron_right"
                            size="1.25rem"
                            color="white"
                          />

                          <FormattedDate
                            value={post.current_revision.event_end_at}
                            day="numeric"
                            month="short"
                            year="numeric"
                          />
                        </Text>
                      </HStack>
                    ) : (
                      <Text variant="menu-dropdown">
                        <FormattedDate
                          value={post.current_revision.event_start_at}
                          day="numeric"
                          month="short"
                          year="numeric"
                        />
                      </Text>
                    )}
                  </Box>
                </Box>
              </DarkMode>
            )}
          </Box>

          <Flex
            flexGrow={1}
            direction="column"
            minH="106px"
            borderLeftWidth="1px"
            borderLeftColor="brandPrimary.500"
            px="12px"
            pt="8px"
            pb="13px"
            _groupHover={{
              borderLeftWidth: "2px",
            }}>
            {(title || summary) && (
              <Stack flexGrow={1} spacing="4px" h="full">
                {title && (
                  <Text fontSize="1.25rem" noOfLines={1}>
                    {translate(title)}
                  </Text>
                )}

                {summary && !isAssociation && (
                  <Text
                    maxH="2.5rem"
                    flexGrow={1}
                    fontSize=".875rem"
                    whiteSpace="pre-line"
                    noOfLines={2}>
                    {translate(summary)}
                  </Text>
                )}

                {isAssociation && (
                  <Stack py=".25rem">
                    {post.current_revision &&
                      post.current_revision.asso_zipcode &&
                      post.current_revision.asso_city && (
                        <HStack>
                          <Icon
                            icon="ms_house"
                            color="gray.400"
                            size="1.5rem"
                          />

                          <Text fontSize="1rem" color="gray.700">
                            <FormattedMessage
                              defaultMessage={"{address} {zipcode} {city}"}
                              values={{
                                address: post.current_revision.asso_address2
                                  ? `${post.current_revision.asso_address1}, ${post.current_revision.asso_address2}`
                                  : post.current_revision.asso_address1,
                                zipcode: post.current_revision.asso_zipcode,
                                city: post.current_revision.asso_city,
                              }}
                            />
                          </Text>
                        </HStack>
                      )}

                    <HStack>
                      {post.current_revision &&
                        post.current_revision.asso_phone_country &&
                        post.current_revision.asso_phone_number && (
                          <HStack flexGrow={1} maxW="280px">
                            <Icon
                              icon="ms_phone"
                              color="gray.400"
                              size="1.5rem"
                            />

                            <Text fontSize="1rem" color="gray.700">
                              <Link
                                href={`tel:${post.current_revision.asso_phone_number}`}
                                display="inline-block">
                                {post.current_revision.asso_phone_number
                                  .replace("+33", "0")
                                  .replace(/(..)/g, "$1 ")}
                              </Link>
                            </Text>
                          </HStack>
                        )}

                      {post.current_revision &&
                        post.current_revision.asso_email && (
                          <HStack flexGrow={1} maxW="260px">
                            <Icon
                              icon="ms_mail"
                              color="gray.400"
                              size="1.5rem"
                            />

                            <Text fontSize="1rem" color="gray.700">
                              <Link href="mailto:labresse@gmail.fr" isExternal>
                                {post.current_revision.asso_email}
                              </Link>
                            </Text>
                          </HStack>
                        )}
                    </HStack>
                  </Stack>
                )}
              </Stack>
            )}

            <HStack pt=".2rem" justify="flex-end" w="full" mt=".5rem">
              <Button
                rightIcon={
                  <Icon icon="ms_chevron_right" color="gray.400" size="1rem" />
                }
                variant="link">
                <Text
                  variant="button-small"
                  fontSize=".75rem"
                  _hover={{ textDecoration: "underline" }}>
                  {isArticle ? (
                    <FormattedMessage defaultMessage="Lire plus" />
                  ) : (
                    <FormattedMessage defaultMessage="En savoir plus" />
                  )}
                </Text>
              </Button>
            </HStack>
          </Flex>
        </Flex>
      </Href>

      {post.categories && post.categories.length > 0 && (
        <Box position="absolute" bottom="8px" left="13px">
          {post.categories.map((category, index) =>
            index === 0 ? ( // limits to 1 category
              <Box key={category.id}>
                <Button
                  maxW="150px"
                  variant="outline"
                  colorScheme="brandPrimary"
                  borderRadius="4px"
                  color="brandPrimary.700"
                  borderColor="brandPrimary.700"
                  size="xs"
                  px="8px"
                  onClick={() => {
                    if (setValue) {
                      setValue("category_id", [category.id?.toString()], {
                        shouldDirty: true,
                      });
                    }
                  }}>
                  <Text variant="button-small" fontSize=".75rem" noOfLines={1}>
                    {translate(category.name)}
                  </Text>
                </Button>
              </Box>
            ) : null,
          )}
        </Box>
      )}
    </Box>
  );
}
